import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { AIConversation, EstateItem } from '../types';
import { MessageSquare, Clock, ChevronRight, Shield, FileText, Phone, Plus, Trash2, Bot } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface VaultProps {
  userId: string;
  householdId: string;
  onOpenChat: (conversationId: string) => void;
}

export default function Vault({ userId, householdId, onOpenChat }: VaultProps) {
  const [conversations, setConversations] = useState<AIConversation[]>([]);
  const [estateItems, setEstateItems] = useState<EstateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'chats' | 'ledger'>('ledger');

  // Add Item Modal State
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemCategory, setNewItemCategory] = useState<'insurance' | 'will' | 'contact' | 'other'>('insurance');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDetails, setNewItemDetails] = useState('');

  const renderMarkdown = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('###')) return <h3 key={i} className="text-lg font-bold mt-4 mb-2 text-slate-800">{line.replace('###', '')}</h3>;
      if (line.startsWith('##')) return <h2 key={i} className="text-xl font-bold mt-6 mb-3 text-indigo-900 border-b pb-1">{line.replace('##', '')}</h2>;
      if (line.startsWith('#')) return <h1 key={i} className="text-2xl font-bold mt-6 mb-3 text-slate-900">{line.replace('#', '')}</h1>;
      if (line.startsWith('-') || line.startsWith('*')) return <li key={i} className="ml-4 list-disc">{line.replace(/^[-*]/, '')}</li>;
      if (line.trim() === '') return <br key={i} />;
      
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <p key={i} className="mb-2">
          {parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={index} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  useEffect(() => {
    const qChats = query(
      collection(db, 'ai_conversations'),
      where('userId', '==', userId)
    );

    const unsubChats = onSnapshot(qChats, (snapshot) => {
      const convs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AIConversation));
      convs.sort((a, b) => {
        const dateA = a.lastMessageTime?.seconds || 0;
        const dateB = b.lastMessageTime?.seconds || 0;
        return dateB - dateA;
      });
      setConversations(convs);
    }, (error) => {
      console.error('Error fetching chats:', error);
    });

    const qEstate = query(
      collection(db, 'estate_items'),
      where('householdId', '==', householdId)
    );

    const unsubEstate = onSnapshot(qEstate, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as EstateItem));
      // Sort client-side to avoid requiring a composite index in Firestore
      items.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });
      setEstateItems(items);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching estate items:', error);
      setLoading(false);
    });

    return () => {
      unsubChats();
      unsubEstate();
    };
  }, [userId, householdId]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemTitle || !newItemDetails) return;

    try {
      await addDoc(collection(db, 'estate_items'), {
        userId,
        householdId,
        category: newItemCategory,
        title: newItemTitle,
        details: newItemDetails,
        createdAt: serverTimestamp()
      });
      setIsAddingItem(false);
      setNewItemTitle('');
      setNewItemDetails('');
    } catch (err) {
      console.error('Error adding estate item:', err);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (confirm('Are you sure you want to delete this secure record?')) {
      await deleteDoc(doc(db, 'estate_items', id));
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'insurance': return <Shield className="w-5 h-5 text-indigo-600" />;
      case 'will': return <FileText className="w-5 h-5 text-amber-600" />;
      case 'contact': return <Phone className="w-5 h-5 text-emerald-600" />;
      case 'advisor_report': return <Bot className="w-5 h-5 text-blue-600" />;
      default: return <Shield className="w-5 h-5 text-slate-600" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-24 px-4 sm:px-0">
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">The Family Vault</h2>
          <p className="text-sm text-slate-500 mt-1">Secure Digital Ledger and AI Strategist History</p>
        </div>
        <div className="flex bg-slate-200/50 p-1 rounded-lg shrink-0">
          <button
            onClick={() => setActiveTab('ledger')}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors ${activeTab === 'ledger' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Digital Ledger
          </button>
          <button
            onClick={() => setActiveTab('chats')}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors ${activeTab === 'chats' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            AI Chats
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-center p-8 text-slate-400">Loading vault...</div>
      ) : activeTab === 'chats' ? (
        conversations.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-700">No conversations yet</h3>
            <p className="text-sm text-slate-500 mt-2">Chat with the AI Strategist to brainstorm goals and set strict priorities. Your chats will be saved here safely.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {conversations.map(conv => (
                <li 
                  key={conv.id} 
                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer group flex items-center justify-between"
                  onClick={() => onOpenChat(conv.id!)}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg shrink-0">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">"{conv.title}"</h4>
                      <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                        <Clock className="w-3 h-3" />
                        {conv.lastMessageTime ? formatDistanceToNow(conv.lastMessageTime.toDate(), { addSuffix: true }) : 'Just now'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </li>
              ))}
            </ul>
          </div>
        )
      ) : (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => setIsAddingItem(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-sm"
            >
              <Plus className="w-4 h-4" /> Add Secure Record
            </button>
          </div>

          {isAddingItem && (
            <div className="bg-white p-6 rounded-xl border border-indigo-100 shadow-lg mb-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-indigo-500"></div>
              <h3 className="text-lg font-bold text-slate-900 mb-4">Add New Record</h3>
              <form onSubmit={handleAddItem} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Category</label>
                  <select 
                    value={newItemCategory}
                    onChange={(e) => setNewItemCategory(e.target.value as any)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  >
                    <option value="insurance">Life/Disability Insurance</option>
                    <option value="will">Will & Testament Location</option>
                    <option value="contact">Emergency Contact</option>
                    <option value="other">Other Secure Note</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                  <input 
                    type="text" 
                    required
                    value={newItemTitle}
                    onChange={(e) => setNewItemTitle(e.target.value)}
                    placeholder="e.g. SunLife Term 20 Policy"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Secure Details</label>
                  <textarea 
                    required
                    value={newItemDetails}
                    onChange={(e) => setNewItemDetails(e.target.value)}
                    placeholder="Policy Number: 123456789. Agent: John Doe (555-0192)."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm min-h-[100px]"
                  />
                </div>
                <div className="flex gap-3 justify-end pt-2">
                  <button type="button" onClick={() => setIsAddingItem(false)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800">Cancel</button>
                  <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm">Save Record</button>
                </div>
              </form>
            </div>
          )}

          {estateItems.length === 0 && !isAddingItem ? (
            <div className="bg-slate-50 rounded-xl border border-slate-200 border-dashed p-12 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-700">Digital Ledger is Empty</h3>
              <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Store critical family information here like Insurance Policies, Will locations, and Emergency Contacts in case of emergency.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {estateItems.map(item => (
                <div key={item.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative group hover:border-indigo-200 transition-colors">
                  {item.category !== 'advisor_report' && (
                    <button
                      onClick={() => handleDeleteItem(item.id!)}
                      className="absolute top-4 right-4 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${item.category === 'insurance' ? 'bg-indigo-50' : item.category === 'will' ? 'bg-amber-50' : item.category === 'contact' ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                      {getCategoryIcon(item.category)}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 leading-tight pr-6">{item.title}</h4>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{item.category}</span>
                    </div>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 mt-2">
                    {item.category === 'advisor_report' ? (
                      <div className="text-sm text-slate-700">
                        {renderMarkdown(item.details)}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-700 whitespace-pre-wrap font-mono text-xs">{item.details}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
